import { useState, useEffect, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import Calendar from "react-calendar";
import dayjs from "dayjs";

import {
  getTreatzSpaDateSlot,
  IResTreatzSpaDateSlot,
  IResTreatzSpaDateSlotItem,
  postTreatzSpaReservation,
} from "@src/api/treatzSpa/treatzSpa";
import { closeWebview } from "@src/utils/route";
import toast from "@src/utils/toast";
import { TreatzSpaProduct, TreatzSpaErrorCode, TreatzSpaErrorMessage } from "./define";

import BookingTitle from "@src/components/booking/bookingTitle/BookingTitle";
import CheckInfoBox from "@src/components/common/applyForm/CheckInfoBox";
import FormContainer from "@src/components/treatzSpa/FormContainer";
import SelectableButtonsGrid, {
  ISelectableButtonMenu,
} from "@src/components/treatzSpa/SelectableButtonsGrid";
import { CustomHeader, Icon, LoadingSpinner, Modal } from "@src/shared/src";

export default function TreatzSpaBookingForm(props: {
  goToCompletePage: (reservationId: number) => void;
}) {
  const [dateSlots, setDateSlots] = useState<IResTreatzSpaDateSlot | null>(null);

  useEffect(() => {
    setupDateSlot();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getDateSlotFromServer(dates: string) {
    try {
      const res = await getTreatzSpaDateSlot(dates);
      return res;
    } catch (error) {
      console.error("Error fetching date slot:", error);
      toast.error("예약 가능 날짜를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
      return null;
    }
  }

  async function setupDateSlot() {
    try {
      const now = dayjs();
      const reqStartDate = now.format("YYYY.MM");
      const reqEndDate = dayjs().add(1, "month").format("YYYY.MM");
      const reqDates = `${reqStartDate},${reqEndDate}`;

      const res = await getDateSlotFromServer(reqDates);
      setDateSlots(res);
    } catch (error) {
      console.error("Error fetching date slot:", error);
      toast.error("예약 가능 날짜를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
    }
  }

  return (
    <div className="treatz-spa-booking-form-page">
      <CustomHeader scrollHide={false} goBack={closeWebview} />

      <BookingTitle title="예약 정보를 선택해주세요." />

      {dateSlots !== null && (
        <FormContents dateSlots={dateSlots} goToCompletePage={props.goToCompletePage} />
      )}

      {/* TODO 임시로 5월 예약 막기, 추후에 슬롯 데이터로 직접 체크하기 */}
      <Modal
        isShow={true}
        text={`5월 트리츠스파 예약이 마감되었습니다.\n자세한 사항은 트리츠스파 카카오톡 채널로 문의 부탁드립니다.`}
        overlay={true}
        overlayValue={0.5}
        buttons={[
          {
            name: "나가기",
            theme: "red500",
            action: () => closeWebview(),
          },
        ]}
      />
    </div>
  );
}

function FormContents(props: {
  dateSlots: IResTreatzSpaDateSlot;
  goToCompletePage: (reservationId: number) => void;
}) {
  const [targetSlotItems, setTargetSlotItems] = useState<IResTreatzSpaDateSlotItem[] | null>(null);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedReservationId, setSelectedReservationId] = useState<number | null>(null);
  const [selectedGuestCount, setSelectedGuestCount] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [isAgree, setIsAgree] = useState<boolean>(false);

  const [isSubmitReservationLoading, setIsSubmitReservationLoading] = useState<boolean>(false);
  const [isCheckWifeModalOpen, setIsCheckWifeModalOpen] = useState<boolean>(false);
  const [isWifeGuideModalOpen, setIsWifeGuideModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // menus
  const [timeMenus, setTimeMenus] = useState<ISelectableButtonMenu[] | null>(null);
  const [guestCountMenus, setGuestCountMenus] = useState<ISelectableButtonMenu[] | null>(null);
  const PRODUCT_MENUS = [
    { id: 1, text: TreatzSpaProduct.WEDDING.name, value: TreatzSpaProduct.WEDDING.value },
    { id: 2, text: TreatzSpaProduct.PRESTIGE.name, value: TreatzSpaProduct.PRESTIGE.value },
  ];

  function onChangeDate(date: Date) {
    setSelectedDate(date);

    // 하위값 초기화
    setSelectedReservationId(null);
    setSelectedGuestCount(null);
    setSelectedProduct(null);
    setIsAgree(false);

    // 날짜에 해당하는 슬롯을 가져온다.
    const dateFormatted = dayjs(date).format("YYYY-MM-DD");
    const _targetSlotItems = props.dateSlots[dateFormatted];
    if (_targetSlotItems === undefined) {
      return;
    }
    setTargetSlotItems(_targetSlotItems);

    // 메뉴 생성
    const timeMenus = _targetSlotItems.map((item) => {
      return {
        id: item.treatzSpaReservationId,
        text: item.visitTime,
        value: item.treatzSpaReservationId,
        isDisabled: item.stock === 0,
      };
    });
    setTimeMenus(timeMenus);
  }

  function onChangeTime(value: number) {
    if (targetSlotItems === null) return;
    setSelectedReservationId(value);

    // 하위값 초기화
    setSelectedGuestCount(null);
    setSelectedProduct(null);
    setIsAgree(false);

    // 메뉴 생성
    const stock = targetSlotItems.find((item) => item.treatzSpaReservationId === value)?.stock || 0;
    const guestCountMenus = [
      {
        id: 1,
        text: `1명`,
        value: 1,
        isDisabled: stock === 0,
      },
      {
        id: 2,
        text: `2명`,
        value: 2,
        isDisabled: stock < 2,
      },
    ];
    setGuestCountMenus(guestCountMenus);
  }

  async function handleClickSubmitButton() {
    if (isAgree === false) return;

    if (
      selectedDate === null ||
      selectedReservationId === null ||
      selectedGuestCount === null ||
      selectedProduct === null
    ) {
      toast.error("예약 정보를 모두 선택해주세요.");
      return;
    }

    setIsCheckWifeModalOpen(true);
  }

  async function submitReservationFromServer() {
    if (isSubmitReservationLoading === true) return;

    setIsSubmitReservationLoading(true);

    try {
      const params = {
        treatzSpaReservationId: selectedReservationId,
        numberOfVisitors: selectedGuestCount,
        productName: selectedProduct,
      };

      const resData = await postTreatzSpaReservation(params);
      props.goToCompletePage(resData.treatzSpaReservationClaimId);

      setIsCheckWifeModalOpen(false);
      setIsWifeGuideModalOpen(false);
    } catch (error) {
      console.error("Error submitting reservation:", error);

      // 약속된 에러인지 확인
      const errorCode = error?.response?.data?.msg;
      const isTreatzSpaError = Object.values(TreatzSpaErrorCode).includes(errorCode);
      console.log("isTreatzSpaError", isTreatzSpaError);
      if (isTreatzSpaError === true) {
        handleTreatzSpaError(errorCode);
        return;
      }

      toast.error("예약 신청에 실패했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsSubmitReservationLoading(false);
    }
  }

  // 약속된 에러 처리
  function handleTreatzSpaError(errorCode: string) {
    let errorMessage = "";

    switch (errorCode) {
      case TreatzSpaErrorCode.OUT_OF_STOCK:
        errorMessage = TreatzSpaErrorMessage.OUT_OF_STOCK;
        break;
      case TreatzSpaErrorCode.RESERVATION_PENDING_EXISTS:
        errorMessage = TreatzSpaErrorMessage.RESERVATION_PENDING_EXISTS;
        break;
    }

    setIsErrorModalOpen(true);
    setErrorMessage(errorMessage);
  }

  function handleResetForm() {
    setTargetSlotItems(null);

    setSelectedDate(null);
    setSelectedReservationId(null);
    setSelectedGuestCount(null);
    setSelectedProduct(null);
    setIsAgree(false);

    setTimeMenus(null);
    setGuestCountMenus(null);

    setIsCheckWifeModalOpen(false);
    setIsWifeGuideModalOpen(false);
    setIsErrorModalOpen(false);
    setErrorMessage("");
  }

  return (
    <div
      className="treatz-spa-reservation-form-wrap"
      style={selectedProduct !== null ? { paddingBottom: "0px" } : {}}
    >
      <StepCalendar
        dateSlots={props.dateSlots}
        selectedDate={selectedDate}
        onChangeDate={(date) => onChangeDate(date)}
      />

      {selectedDate !== null && timeMenus !== null && (
        <FormContainer hasFadeInAnimation={true} label="시작 시간 선택" isRequired={true}>
          <SelectableButtonsGrid
            column={3}
            menus={timeMenus}
            currentValue={selectedReservationId}
            onClickButton={(value) => onChangeTime(value as number)}
          />
        </FormContainer>
      )}

      {selectedReservationId !== null && guestCountMenus !== null && (
        <FormContainer hasFadeInAnimation={true} label="예약 인원 수" isRequired={true}>
          <SelectableButtonsGrid
            column={2}
            menus={guestCountMenus}
            currentValue={selectedGuestCount}
            onClickButton={(value) => setSelectedGuestCount(value as number)}
          />
        </FormContainer>
      )}

      {selectedGuestCount !== null && (
        <FormContainer hasFadeInAnimation={true} label="이용 상품 선택" isRequired={true}>
          <SelectableButtonsGrid
            column={2}
            menus={PRODUCT_MENUS}
            currentValue={selectedProduct}
            onClickButton={(value) => setSelectedProduct(value as string)}
          />
        </FormContainer>
      )}

      {selectedProduct !== null && (
        <div className="fade-in">
          <div className="info-text-box">
            <CheckInfoBox
              title="방문 예약 전 확인해주세요."
              descriptionType="list"
              description={[
                "신청해주신 내용은 트리츠스파에서 확인 후 최종 확정되며, 예약 확정 시 알림톡이 발송됩니다.",
                "예약 후 변경이 필요한 경우 꼭 트리츠스파에 연락해주세요. 단, 인원 수 조정은 불가합니다.",
              ]}
            />
          </div>

          <div className="checkbox-row">
            <div className="checkbox" onClick={() => setIsAgree(!isAgree)}>
              <Icon name={isAgree ? "CheckboxChecked" : "CheckboxDefault"} size={24} />
              <p className="label-text">
                (필수) 트리츠스파에 고객 정보 전달 및 방문예약에 동의합니다.
              </p>
            </div>
          </div>

          <div className="bottom-button-box">
            <button
              onClick={handleClickSubmitButton}
              className={`
                btn btn-giant btn-full
                ${isAgree === true ? "btn-red500" : "btn-disabled"}
              `}
            >
              사전 예약하기
            </button>
          </div>
        </div>
      )}

      {isCheckWifeModalOpen === true && (
        <CheckWifeModal
          isOpen={isCheckWifeModalOpen}
          onClickNo={() => {
            setIsCheckWifeModalOpen(false);
            setIsWifeGuideModalOpen(true);
          }}
          onClickYes={submitReservationFromServer}
        />
      )}

      {isWifeGuideModalOpen === true && (
        <WifeGuideModal
          isOpen={isWifeGuideModalOpen}
          onClose={() => setIsWifeGuideModalOpen(false)}
        />
      )}

      {isErrorModalOpen === true && (
        <ErrorModal
          isOpen={isErrorModalOpen}
          errorMessage={errorMessage}
          onClose={() => handleResetForm()}
        />
      )}

      {isSubmitReservationLoading === true && <LoadingSpinner />}
    </div>
  );
}

function StepCalendar(props: {
  dateSlots: IResTreatzSpaDateSlot;
  selectedDate: Date | null;
  onChangeDate: (date: Date) => void;
}) {
  const { dateSlots, selectedDate, onChangeDate } = props;
  const dateSlotArr = Object.keys(dateSlots);

  const calendar = useRef<HTMLInputElement | null>(null);

  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date | null>(null);

  useEffect(() => {
    const now = dayjs();
    const _minDate = now.toDate();
    setMinDate(_minDate);

    const sortedDateSlotArr = dateSlotArr.sort((a, b) => {
      return dayjs(a).diff(dayjs(b), "day");
    });
    const maxDateStr = sortedDateSlotArr[sortedDateSlotArr.length - 1];
    const _maxDate = dayjs(maxDateStr).toDate();

    const diff = dayjs(_maxDate).diff(dayjs(_minDate), "day");
    if (diff >= 0) {
      setMaxDate(_maxDate);
    } else {
      setMaxDate(_minDate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const swiperHandlers = useSwipeable({
    onSwipedLeft(_) {
      const btn = calendar.current?.getElementsByClassName(
        "react-calendar__navigation__next-button",
      )[0];
      if (btn) {
        (btn as HTMLButtonElement).click();
      }
    },
    onSwipedRight(_) {
      const btn = calendar.current?.getElementsByClassName(
        "react-calendar__navigation__prev-button",
      )[0];
      if (btn) {
        (btn as HTMLButtonElement).click();
      }
    },
  });

  return (
    <div className="calendar-box" {...swiperHandlers}>
      <Calendar
        minDate={minDate}
        maxDate={maxDate}
        calendarType="US"
        onClickDay={(date, event) => {
          onChangeDate(date);
        }}
        tileClassName={({ activeStartDate, date, view }) => {
          const dateFormatted = dayjs(date).format("YYYY-MM-DD");
          const selectedDateFormatted = dayjs(selectedDate).format("YYYY-MM-DD");
          if (selectedDateFormatted === dateFormatted) {
            return "active";
          } else {
            return null;
          }
        }}
        minDetail="month"
        tileContent={({ activeStartDate, date, view }) => {
          if (view === "month" && dayjs().isSame(date, "day")) {
            return <span className="today">오늘</span>;
          }
          return view === "month" ? <span>{date.getDate()}</span> : null;
        }}
        tileDisabled={({ date, view }) => {
          const dateFormatted = dayjs(date).format("YYYY-MM-DD");
          if (dateSlotArr.indexOf(dateFormatted) > -1) {
            return false;
          }
          return true;
        }}
        formatDay={(_, date) => ""}
        inputRef={calendar}
        nextLabel={<Icon name="ChevronrightG900" size={18} />}
        prevLabel={<Icon name="ChevronleftG900" size={18} />}
        // prevLabel={<Prev />}
        next2Label={null}
        prev2Label={null}
      />
    </div>
  );
}

function CheckWifeModal(props: { isOpen: boolean; onClickNo: () => void; onClickYes: () => void }) {
  return (
    <Modal
      isShow={props.isOpen}
      text={`트리츠스파 예약은 신부님 이름으로 가능합니다.\n예약자가 신부님이 맞으신가요?`}
      overlay={true}
      overlayValue={0.5}
      buttons={[
        {
          name: "아니오",
          theme: "gray50",
          action: () => props.onClickNo(),
        },
        {
          name: "예",
          theme: "red500",
          action: () => props.onClickYes(),
        },
      ]}
    />
  );
}

function WifeGuideModal(props: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isShow={props.isOpen}
      text={`신부님 아이디로 예약 부탁드립니다.\n감사합니다.`}
      overlay={true}
      overlayValue={0.5}
      buttons={[
        {
          name: "닫기",
          theme: "red500",
          action: () => props.onClose(),
        },
      ]}
    />
  );
}

function ErrorModal(props: { isOpen: boolean; errorMessage: string; onClose: () => void }) {
  return (
    <Modal
      isShow={props.isOpen}
      text={props.errorMessage}
      overlay={true}
      overlayValue={0.5}
      buttons={[
        {
          name: "닫기",
          theme: "red500",
          action: () => props.onClose(),
        },
      ]}
    />
  );
}
